import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { isClosedToday } from './vendor';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('9troride_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [vendorNotes, setVendorNotes] = useState(() => {
    try {
      const saved = localStorage.getItem('9troride_vendorNotes');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [vendorsInfo, setVendorsInfo] = useState({});

  useEffect(() => {
    const chefsRef = ref(db, 'chefs');
    const vendorsRef = ref(db, 'vendors');

    const unsubscribeChefs = onValue(chefsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setVendorsInfo(prev => ({ ...prev, ...data }));
    }, (error) => console.error("Failed to fetch chefs in CartContext:", error));

    const unsubscribeVendors = onValue(vendorsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setVendorsInfo(prev => ({ ...prev, ...data }));
    }, (error) => console.error("Failed to fetch vendors in CartContext:", error));

    return () => {
      unsubscribeChefs();
      unsubscribeVendors();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('9troride_cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('9troride_vendorNotes', JSON.stringify(vendorNotes));
  }, [vendorNotes]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id && JSON.stringify(i.selectedExtras) === JSON.stringify(item.selectedExtras) && JSON.stringify(i.selectedSides || []) === JSON.stringify(item.selectedSides || []));
      if (existing) {
        return prev.map(i => i === existing ? { ...i, quantity: i.quantity + item.quantity } : i);
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (itemId, extras, sides) => {
    setCart(prev => prev.filter(i => !(i.id === itemId && JSON.stringify(i.selectedExtras) === JSON.stringify(extras) && JSON.stringify(i.selectedSides || []) === JSON.stringify(sides || []))));
  };

  const updateQuantity = (itemId, extras, sides, delta) => {
    setCart(prev => prev.map(i => {
      if (i.id === itemId && JSON.stringify(i.selectedExtras) === JSON.stringify(extras) && JSON.stringify(i.selectedSides || []) === JSON.stringify(sides || [])) {
        return { ...i, quantity: i.quantity + delta };
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const clearCart = () => setCart([]);

  const clearVendorCart = (vendorId) => {
    setCart(prev => prev.filter(i => i.vendorId !== vendorId));
  };

  const clearAllCartData = () => {
    setCart([]);
    setVendorNotes({});
  };

  const updateVendorNote = (vendorId, note) => {
    setVendorNotes(prev => ({
      ...prev,
      [vendorId]: note,
    }));
  };

  const getVendorNote = (vendorId) => {
    return vendorNotes[vendorId] || '';
  };

  const filteredCart = useMemo(() => {
    return cart.filter(item => {
      if (!item.vendorId) return true;
      const dbVendor = vendorsInfo[item.vendorId];
      if (dbVendor && dbVendor.workingHours) {
        return !isClosedToday(dbVendor.workingHours);
      }
      if (item.vendor && item.vendor.workingHours) {
        return !isClosedToday(item.vendor.workingHours);
      }
      return true;
    });
  }, [cart, vendorsInfo]);

  const cartItemCount = filteredCart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = filteredCart.reduce((sum, item) => sum + (item.totalNumericPrice * item.quantity), 0);

  return (
    <CartContext.Provider value={{ cart: filteredCart, addToCart, removeFromCart, updateQuantity, clearCart, cartItemCount, cartTotal, clearVendorCart, vendorNotes, updateVendorNote, getVendorNote, clearAllCartData }}>
      {children}
    </CartContext.Provider>
  );
};